import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import { publishSidebar, registerSidebarSetters } from '../../../stores/chartSidebarStore';


const CATEGORY_LABELS = {
  candlestick:      'Candlestick',
  chart_pattern:    'Chart Patterns',
  trend_ma:         'Trend / MA',
  oscillator:       'Oscillator',
  volume:           'Volume',
  market_structure: 'Market Structure',
  boundary:         'Boundary',
  statistical:      'Statistical',
  levels:           'Levels',
};

const CATEGORY_ORDER = [
  'chart_pattern', 'market_structure', 'trend_ma',
  'oscillator', 'volume', 'boundary', 'levels', 'statistical', 'candlestick',
];

const DIR_COLOR = {
  bullish: 'text-green-400',
  bearish: 'text-red-400',
};

// Plan 126 — return-horizon options for the per-row avg-forward-return display.
// `days` is also used for the "recently occurred" highlight window.
const RETURN_HORIZONS = [
  { value: '1d', label: '1D', days: 1 },
  { value: '3d', label: '3D', days: 3 },
  { value: '7d', label: '7D', days: 7 },
  { value: '1m', label: '1M', days: 30 },
  { value: '3m', label: '3M', days: 90 },
  { value: '6m', label: '6M', days: 180 },
  { value: '1y', label: '1Y', days: 365 },
];

const HORIZON_DAYS = Object.fromEntries(RETURN_HORIZONS.map(h => [h.value, h.days]));
const MIN_SAMPLES_FOR_HIGHLIGHT = 3;  // n=1 averages can mislead; require ≥3.

function formatAvgReturn(pct) {
  if (pct === null || pct === undefined) return null;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function relativeDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.round((now - d) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 30) return `${diffDays}d ago`;
  const months = Math.round(diffDays / 30);
  return `${months}mo ago`;
}

export default function PatternsTab({ currentSymbol, chartRef }) {
  const [types, setTypes] = useState([]);
  // Plan 126: events cached per event_type, lazy-fetched on toggle. Avoids
  // truncation — for an event type that fires 2k times across 5 years on a
  // hot symbol, the filtered list fits comfortably in one request.
  const [eventsByType, setEventsByType] = useState({});  // { event_type: [events] }
  const [activeTypes, setActiveTypes] = useState(new Set());
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('all');
  const [loading, setLoading] = useState(false);
  const [markersOn, setMarkersOn] = useState(false);
  // Plan 126: per-type metadata (count, last_occurred, direction, returns)
  // from the patterns endpoint (full-history GROUP BY).
  const [patternsByType, setPatternsByType] = useState({});
  const [returnHorizon, setReturnHorizon] = useState('3m');
  const prevSymbol = useRef(null);

  // Fetch available event types once
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/calculated-events/types/`)
      .then(r => r.json())
      .then(d => setTypes(d.types || []))
      .catch(() => {});
  }, []);

  // Fetch events whenever symbol or date horizon changes
  useEffect(() => {
    if (!currentSymbol) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    if (prevSymbol.current && prevSymbol.current !== currentSymbol) {
      chartRef?.current?.clearEventMarkers();
      setMarkersOn(false);
      setActiveTypes(new Set());
      setEventsByType({});  // drop per-type event cache on symbol change
    }
    prevSymbol.current = currentSymbol;

    // Plan 126: per-type aggregates (full history) — drives the sidebar row
    // metadata: count (×N), last_occurred_at (Xd ago), direction, and the
    // forward-return number at each horizon. Horizon switching is client-side.
    setLoading(true);
    fetch(
      `${API_BASE_URL}/api/calculated-events/patterns/?symbol=${encodeURIComponent(currentSymbol)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(r => r.json())
      .then(d => {
        const map = {};
        (d.event_types || []).forEach(row => { map[row.event_type] = row; });
        setPatternsByType(map);
      })
      .catch(() => setPatternsByType({}))
      .finally(() => setLoading(false));
  }, [currentSymbol]);

  // Plan 126: lazy-fetch this event_type's events when toggled on for the
  // first time. Filtered request → small payload, full history.
  const fetchEventsForType = useCallback((eventType) => {
    if (eventsByType[eventType]) return;  // already cached
    const token = localStorage.getItem('token');
    if (!token || !currentSymbol) return;
    fetch(
      `${API_BASE_URL}/api/calculated-events/?symbol=${encodeURIComponent(currentSymbol)}` +
      `&event_type=${encodeURIComponent(eventType)}&limit=10000`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(r => r.json())
      .then(d => setEventsByType(prev => ({ ...prev, [eventType]: d.events || [] })))
      .catch(() => setEventsByType(prev => ({ ...prev, [eventType]: [] })));
  }, [currentSymbol, eventsByType]);

  // Rebuild chart markers from the per-type event cache whenever active types,
  // direction, or cache contents change.
  useEffect(() => {
    if (!chartRef?.current) return;
    if (activeTypes.size === 0) {
      chartRef.current.clearEventMarkers();
      setMarkersOn(false);
      return;
    }
    const visible = [];
    activeTypes.forEach(et => {
      (eventsByType[et] || []).forEach(e => visible.push(e));
    });
    chartRef.current.createEventMarkers(visible);
    setMarkersOn(visible.length > 0);
  }, [activeTypes, eventsByType, chartRef]);

  // plan 181: mirror pattern state into the sidebar bus so the CLI
  // (window.__brochart getSidebar) can read available types + selection.
  // plan 181 fix (#7): publish `selected` from its OWN effect keyed on activeTypes
  // ONLY. Previously selected was republished whenever types/patternsByType resolved
  // too — so an async fetch landing mid-`patterns select` clobbered the just-set
  // selection with the stale (often empty) activeTypes, making the CLI's select
  // silently not persist despite reporting success. Keeping the two concerns
  // separate means selected only ever changes when activeTypes actually changes.
  useEffect(() => {
    publishSidebar({ patterns: { selected: Array.from(activeTypes) } });
  }, [activeTypes]);

  useEffect(() => {
    publishSidebar({ patterns: {
      availableTypes: types.map(t => ({ event_type: t.event_type, display_name: t.display_name, category: t.event_category })),
      aggregates: patternsByType,
      direction,
      horizon: returnHorizon,
    } });
  }, [types, patternsByType, direction, returnHorizon]);

  // plan 181: register the same setters the human UI uses so selectPatterns() drives
  // markers identically (bulk-set fetches each type's events, then sets the active set).
  useEffect(() => {
    registerSidebarSetters({
      setPatternSelection: (next) => {
        const arr = Array.isArray(next) ? next : [];
        arr.forEach(t => fetchEventsForType(t));
        setActiveTypes(new Set(arr));
      },
      setPatternDirection: (d) => setDirection(d),
      setPatternHorizon: (h) => setReturnHorizon(h),
    });
  }, [fetchEventsForType]);

  // Plan 126: row counts and last-occurred come from the patterns endpoint
  // (full-history aggregate), NOT the truncated events list.
  const countByType = {};
  const latestByType = {};
  Object.entries(patternsByType).forEach(([et, row]) => {
    countByType[et] = row.count || 0;
    latestByType[et] = { occurred_at: row.last_occurred_at, direction: row.direction };
  });

  // Plan 126 highlight: rows whose avg return at the current horizon beats the
  // average across their direction (bullish/bearish) AND occurred recently
  // enough that the return window is still relevant. Bearish returns are
  // already sign-flipped server-side, so for both directions a HIGHER value
  // means "pattern played out as expected."
  const horizonField = `avg_return_${returnHorizon}`;
  const sampleField = `sample_size_${returnHorizon}`;
  const horizonDays = HORIZON_DAYS[returnHorizon] || 90;
  const recencyDays = Math.max(horizonDays, 30);  // tight horizons still get a 30d recency floor

  const dirReturns = { bullish: [], bearish: [] };
  Object.values(patternsByType).forEach(row => {
    if (!row.direction || !row.returns) return;
    const v = row.returns[horizonField];
    const n = row.returns[sampleField];
    if (v === null || v === undefined || !n) return;
    if (dirReturns[row.direction]) dirReturns[row.direction].push(v);
  });
  const dirAvg = {
    bullish: dirReturns.bullish.length
      ? dirReturns.bullish.reduce((s, v) => s + v, 0) / dirReturns.bullish.length
      : null,
    bearish: dirReturns.bearish.length
      ? dirReturns.bearish.reduce((s, v) => s + v, 0) / dirReturns.bearish.length
      : null,
  };

  const highlightedTypes = new Set();
  const now = Date.now();
  Object.entries(patternsByType).forEach(([et, row]) => {
    if (!row.direction || !row.returns) return;
    const v = row.returns[horizonField];
    const n = row.returns[sampleField];
    if (v === null || v === undefined || !n || n < MIN_SAMPLES_FOR_HIGHLIGHT) return;
    const avg = dirAvg[row.direction];
    if (avg === null || v <= avg) return;
    const lastTs = row.last_occurred_at ? Date.parse(row.last_occurred_at) : 0;
    if (!lastTs) return;
    const ageDays = (now - lastTs) / 86400000;
    if (ageDays > recencyDays) return;
    highlightedTypes.add(et);
  });

  const grouped = {};
  types.forEach(t => {
    if (search) {
      const q = search.toLowerCase();
      const matchesRaw = t.event_type.toLowerCase().includes(q);
      const matchesLabel = (t.display_name || '').toLowerCase().includes(q);
      if (!matchesRaw && !matchesLabel) return;
    }
    // Plan 126: direction filter acts on the sidebar list (not the chart).
    // Bull / Bear hide all rows of the opposite direction (and neutral rows).
    if (direction !== 'all') {
      const rowDir = patternsByType[t.event_type]?.direction;
      if (rowDir !== direction) return;
    }
    const cat = t.event_category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });

  const toggleType = useCallback((eventType) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(eventType)) {
        next.delete(eventType);
      } else {
        next.add(eventType);
        fetchEventsForType(eventType);
      }
      return next;
    });
  }, [fetchEventsForType]);

  const toggleCategory = useCallback((cat, catTypes) => {
    const allOn = catTypes.every(et => activeTypes.has(et));
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (allOn) {
        catTypes.forEach(et => next.delete(et));
      } else {
        catTypes.forEach(et => {
          if (countByType[et]) {
            next.add(et);
            fetchEventsForType(et);
          }
        });
      }
      return next;
    });
  }, [activeTypes, countByType, fetchEventsForType]);

  const clearAll = () => {
    setActiveTypes(new Set());
    chartRef?.current?.clearEventMarkers();
    setMarkersOn(false);
  };

  if (!currentSymbol) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 py-12 text-center">
        <p className="text-xs text-gray-500">Open a symbol on the chart to see its events.</p>
      </div>
    );
  }

  const orderedCats = CATEGORY_ORDER.filter(c => grouped[c]);
  const extraCats = Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c));

  return (
    <div className="flex flex-col h-full text-xs text-gray-300">
      {/* Search */}
      <div className="px-2 pt-2 pb-1.5 flex-shrink-0">
        <div className="flex items-center gap-1.5 bg-gray-700/50 rounded px-2 py-1">
          <Search size={11} className="text-gray-500 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search event types…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-xs text-gray-200 placeholder-gray-600 outline-none min-w-0"
          />
        </div>
      </div>

      {/* Direction + horizon filters */}
      <div className="px-2 pb-2 flex gap-1.5 flex-shrink-0">
        {['all', 'bullish', 'bearish'].map(d => (
          <button
            key={d}
            onClick={() => setDirection(d)}
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
              direction === d
                ? d === 'bullish' ? 'bg-green-600/80 text-white'
                  : d === 'bearish' ? 'bg-red-600/80 text-white'
                  : 'bg-blue-600/80 text-white'
                : 'bg-gray-700/50 text-gray-400 hover:text-gray-200'
            }`}
          >
            {d === 'all' ? 'All' : d === 'bullish' ? '↑ Bull' : '↓ Bear'}
          </button>
        ))}
        <select
          value={returnHorizon}
          onChange={e => setReturnHorizon(e.target.value)}
          title="Forward-return horizon — average % move after each occurrence"
          className="ml-auto bg-gray-700/50 text-gray-400 text-[10px] rounded px-1.5 py-0.5 outline-none"
        >
          {RETURN_HORIZONS.map(h => (
            <option key={h.value} value={h.value}>{h.label}</option>
          ))}
        </select>
      </div>

      {/* Status bar */}
      <div className="px-2 pb-1.5 flex items-center justify-between flex-shrink-0 border-b border-gray-700/60">
        <span className="text-[10px] text-gray-500">
          {loading ? 'Loading…' : `${Object.values(patternsByType).reduce((s, r) => s + (r.count || 0), 0)} events · ${currentSymbol}`}
        </span>
        <div className="flex items-center gap-2">
          {highlightedTypes.size > 0 && (
            <span
              className="text-[10px] text-amber-400 flex items-center gap-1"
              title="Highlighted rows: above-average forward return for their direction, AND occurred recently relative to the selected horizon"
            >
              <span className="inline-block w-0.5 h-2.5 bg-amber-400" />
              {highlightedTypes.size} hot
            </span>
          )}
          {activeTypes.size > 0 && (
            <button onClick={clearAll} className="text-[10px] text-gray-500 hover:text-red-400 transition-colors">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Event type list */}
      <div className="flex-1 overflow-y-auto">
        {[...orderedCats, ...extraCats].map(cat => {
          const catTypes = grouped[cat] || [];
          const isCollapsed = collapsed[cat];
          const catTypeNames = catTypes.map(t => t.event_type);
          const catHasActive = catTypeNames.some(et => activeTypes.has(et));
          const catEventCount = catTypeNames.reduce((s, et) => s + (countByType[et] || 0), 0);

          return (
            <div key={cat} className="border-b border-gray-700/40">
              <button
                className="w-full flex items-center px-2 py-1.5 hover:bg-gray-700/30 transition-colors"
                onClick={() => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }))}
              >
                {isCollapsed
                  ? <ChevronRight size={11} className="text-gray-500 flex-shrink-0 mr-1" />
                  : <ChevronDown size={11} className="text-gray-500 flex-shrink-0 mr-1" />
                }
                <span className={`font-semibold uppercase tracking-wider text-[10px] flex-1 text-left ${catHasActive ? 'text-blue-400' : 'text-gray-400'}`}>
                  {CATEGORY_LABELS[cat] || cat}
                </span>
                {catEventCount > 0 && (
                  <span className="text-[10px] text-gray-600">{catEventCount}</span>
                )}
                {catHasActive && (
                  <button
                    onClick={e => { e.stopPropagation(); toggleCategory(cat, catTypeNames); }}
                    className="ml-2 text-[10px] text-blue-400 hover:text-blue-300"
                  >
                    off
                  </button>
                )}
              </button>

              {!isCollapsed && catTypes.map(t => {
                const count = countByType[t.event_type] || 0;
                const isOn = activeTypes.has(t.event_type);
                const latest = latestByType[t.event_type];
                const hasData = count > 0;
                const isHighlighted = highlightedTypes.has(t.event_type);

                return (
                  <button
                    key={t.event_type}
                    disabled={!hasData}
                    onClick={() => hasData && toggleType(t.event_type)}
                    title={isHighlighted
                      ? `Recent + above-average ${latest?.direction} return at ${returnHorizon.toUpperCase()}`
                      : undefined}
                    className={`w-full flex items-center px-3 py-1 transition-colors text-left ${
                      hasData
                        ? isOn ? 'bg-blue-600/15 hover:bg-blue-600/25' : 'hover:bg-gray-700/30'
                        : 'opacity-25 cursor-default'
                    } ${isHighlighted ? 'border-l-2 border-amber-400 bg-amber-400/5' : 'border-l-2 border-transparent'}`}
                  >
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mr-2 transition-colors ${
                      isOn ? 'bg-blue-400' : hasData ? 'bg-gray-600' : 'bg-gray-700'
                    }`} />
                    <span className={`flex-1 min-w-0 truncate ${isOn ? 'text-gray-200' : hasData ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t.display_name || t.event_type}
                    </span>
                    {latest && (
                      <span className={`ml-1.5 flex-shrink-0 ${DIR_COLOR[latest.direction] || 'text-gray-500'}`}>
                        {latest.direction === 'bullish' ? '↑' : latest.direction === 'bearish' ? '↓' : '·'}
                      </span>
                    )}
                    {latest && (
                      <span className="ml-1 text-gray-600 flex-shrink-0 text-[10px]">
                        {relativeDate(latest.occurred_at)}
                      </span>
                    )}
                    {count > 0 && (
                      <span className="ml-1.5 text-[10px] text-gray-600 flex-shrink-0">×{count}</span>
                    )}
                    {(() => {
                      // Plan 126: render avg forward return at the selected horizon.
                      const row = patternsByType[t.event_type];
                      const returns = row && row.returns;
                      if (!returns) return null;
                      const avg = returns[`avg_return_${returnHorizon}`];
                      const n = returns[`sample_size_${returnHorizon}`];
                      if (avg === null || avg === undefined || !n) return null;
                      const formatted = formatAvgReturn(avg);
                      const colorClass = avg >= 0 ? 'text-green-400' : 'text-red-400';
                      return (
                        <span
                          className={`ml-1.5 text-[10px] flex-shrink-0 ${colorClass} ${isHighlighted ? 'font-semibold' : ''}`}
                          title={`n=${n} occurrence${n === 1 ? '' : 's'}, avg forward return at ${returnHorizon.toUpperCase()}`}
                        >
                          {formatted}
                        </span>
                      );
                    })()}
                  </button>
                );
              })}
            </div>
          );
        })}

        {Object.keys(grouped).length === 0 && !loading && (
          <div className="px-3 py-6 text-center text-gray-600 text-[11px]">
            No events match your filter.
          </div>
        )}
      </div>

      {markersOn && (
        <div className="px-2 py-1.5 border-t border-gray-700/60 flex-shrink-0 bg-blue-600/10 text-[10px] text-blue-400">
          {activeTypes.size} type{activeTypes.size !== 1 ? 's' : ''} overlaid on chart
        </div>
      )}
    </div>
  );
}
